import { makeObservable, observable, action, runInAction } from 'mobx';
import { EventJudgeEventHomeDTO, EventJudgeModel, UpdateJudgeProfileRequest } from 'API/Generated/data-contracts';
import { EventJudge } from 'API/Generated/EventJudge';
import { localScoreRepository } from 'Repositories/Local/localScoreRepository';
import { Tabulation } from 'API/Generated/Tabulation';

const JUDGE_SESSION_EXPIRES_AT = 'judgeSessionExpiresAt';
const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

class EventJudgeStore {
  EventJudgeService: EventJudge = new EventJudge();

TabulationService: Tabulation =
  new Tabulation();
  CurrentJudge: EventJudgeModel | null = null;
  JudgeList: EventJudgeModel[] = [];
JudgeEventHome: EventJudgeEventHomeDTO | null = null;

  loading = false;
  error: string | null = null;

 constructor() {
  makeObservable(this, {
    CurrentJudge: observable,
    JudgeList: observable,
    JudgeEventHome: observable,
    loading: observable,
    error: observable,

    setCurrentJudge: action,
    clearCurrentJudge: action,
    hydrateJudgeFromSession: action,
    logoutJudge: action,
    getJudgeById: action,
    getJudgesByEventId: action,
    addJudgeToEvent: action,
    updateJudge: action,
    deleteJudge: action,
    resetAccessCode: action,
    loginWithAccessCode: action,
    updateLoggedInJudgeProfile: action,
    logoutInactiveJudgeAndClearSyncedData: action,

    finishSideAwards: action,
  });

  this.hydrateJudgeFromSession();
}

  
setCurrentJudge(judge: EventJudgeModel | null) {
  this.CurrentJudge = judge;

  if (judge) {
    sessionStorage.setItem('currentJudge', JSON.stringify(judge));
    sessionStorage.setItem(JUDGE_SESSION_EXPIRES_AT, String(Date.now() + SIX_HOURS_MS));
  } else {
    sessionStorage.removeItem('currentJudge');
    sessionStorage.removeItem(JUDGE_SESSION_EXPIRES_AT);
  }
}
  clearCurrentJudge() {
    this.CurrentJudge = null;
    sessionStorage.removeItem('currentJudge');
  }

hydrateJudgeFromSession() {
  const expiresAt = Number(sessionStorage.getItem(JUDGE_SESSION_EXPIRES_AT));

  if (expiresAt && Date.now() > expiresAt) {
    this.logoutJudge();
    return;
  }

  const raw = sessionStorage.getItem('currentJudge');

  if (!raw) {
    this.CurrentJudge = null;
    return;
  }

  try {
    const judge = JSON.parse(raw) as EventJudgeModel;
    this.CurrentJudge = judge;
  } catch (error) {
    console.error('Error hydrating judge from session:', error);
    this.logoutJudge();
  }
}

logoutJudge() {
  this.CurrentJudge = null;
  this.error = null;
  sessionStorage.removeItem('currentJudge');
  sessionStorage.removeItem(JUDGE_SESSION_EXPIRES_AT);
}

async logoutJudgeHard() {
  const currentJudge = this.CurrentJudge;

  this.CurrentJudge = null;
  this.error = null;

  sessionStorage.removeItem('currentJudge');
  sessionStorage.removeItem(JUDGE_SESSION_EXPIRES_AT);

  try {
    await localScoreRepository.deleteJudgeSession?.(
      Number(currentJudge?.id ?? 0),
    );
  } catch (error) {
    console.warn('Failed clearing local judge login session', error);
  }
}

  async getJudgeById(eventJudgeId: number): Promise<EventJudgeModel | null> {
    this.loading = true;
    this.error = null;

    try {
      const result = await this.EventJudgeService.eventJudgeDetail(eventJudgeId);

      runInAction(() => {
        this.CurrentJudge = result?.data ?? null;

        if (result?.data) {
          sessionStorage.setItem('currentJudge', JSON.stringify(result.data));
        }
      });

      return result?.data ?? null;
    } catch (error) {
      console.error('Error fetching judge by id:', error);
      runInAction(() => {
        this.error = 'Failed to load judge.';
      });
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async getJudgesByEventId(eventId: number): Promise<EventJudgeModel[]> {
    this.loading = true;
    this.error = null;

    try {
      const result = await this.EventJudgeService.eventDetail(eventId);

      runInAction(() => {
        this.JudgeList = result?.data ?? [];
      });

      return result?.data ?? [];
    } catch (error) {
      console.error('Error fetching judges by event id:', error);
      runInAction(() => {
        this.error = 'Failed to load judges.';
        this.JudgeList = [];
      });
      return [];
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async addJudgeToEvent(model: EventJudgeModel): Promise<EventJudgeModel | null> {
    this.loading = true;
    this.error = null;

    try {
      const result = await this.EventJudgeService.eventJudgeCreate(model);

      runInAction(() => {
        this.CurrentJudge = result?.data ?? null;

        if (result?.data) {
          this.JudgeList.push(result.data);
        }
      });

      return result?.data ?? null;
    } catch (error) {
      console.error('Error adding judge to event:', error);
      runInAction(() => {
        this.error = 'Failed to add judge.';
      });
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async updateJudge(model: EventJudgeModel): Promise<EventJudgeModel | null> {
    this.loading = true;
    this.error = null;

    try {
      const result = await this.EventJudgeService.eventJudgeUpdate(model);

      runInAction(() => {
        this.CurrentJudge = result?.data ?? null;

        if (result?.data) {
          this.JudgeList = this.JudgeList.map((judge) =>
            judge.id === result.data?.id ? result.data : judge
          );
        }
      });

      return result?.data ?? null;
    } catch (error) {
      console.error('Error updating judge:', error);
      runInAction(() => {
        this.error = 'Failed to update judge.';
      });
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async deleteJudge(eventJudgeId: number): Promise<boolean> {
    this.loading = true;
    this.error = null;

    try {
      await this.EventJudgeService.eventJudgeDelete(eventJudgeId);

      runInAction(() => {
        this.JudgeList = this.JudgeList.filter((judge) => judge.id !== eventJudgeId);

        if (this.CurrentJudge?.id === eventJudgeId) {
          this.CurrentJudge = null;
          sessionStorage.removeItem('currentJudge');
        }
      });

      return true;
    } catch (error) {
      console.error('Error deleting judge:', error);
      runInAction(() => {
        this.error = 'Failed to delete judge.';
      });
      return false;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async resetAccessCode(eventJudgeId: number): Promise<string | null> {
    this.loading = true;
    this.error = null;

    try {
      const result = await this.EventJudgeService.resetAccessCodeCreate(eventJudgeId);
      return (result?.data as any)?.accessCode ?? null;
    } catch (error) {
      console.error('Error resetting access code:', error);
      runInAction(() => {
        this.error = 'Failed to reset access code.';
      });
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async loginWithAccessCode(accessCode: string): Promise<EventJudgeModel | null> {
    this.loading = true;
    this.error = null;

    try {
      const normalizedCode = accessCode.trim().toUpperCase();

      const result = await this.EventJudgeService.loginCreate({
        accessCode: normalizedCode,
      });

      const judge = result?.data ?? null;

      runInAction(() => {
        this.CurrentJudge = judge;

    this.setCurrentJudge(judge);
      });

      return judge;
} catch (error) {
  console.warn('Online judge login failed.', error);

  if (navigator.onLine) {
    await localScoreRepository.deleteJudgeSessionByAccessCode?.(accessCode);

    runInAction(() => {
      this.error = 'Invalid code or access expired.';
      this.CurrentJudge = null;
      sessionStorage.removeItem('currentJudge');
      sessionStorage.removeItem(JUDGE_SESSION_EXPIRES_AT);
    });

    return null;
  }

  console.warn('Browser is offline. Checking offline judge session.');

  const offlineSession =
    await localScoreRepository.getJudgeSessionByAccessCode(accessCode);

  if (offlineSession) {
    const offlineJudge = {
      id: offlineSession.eventJudgeId,
      eventId: offlineSession.eventId,
      pageantId: offlineSession.pageantId,
      judgeName: offlineSession.judgeName,
      judgeLabel: offlineSession.judgeLabel,
    } as EventJudgeModel;

    runInAction(() => {
      this.error = null;
      this.setCurrentJudge(offlineJudge);
    });

    return offlineJudge;
  }

  runInAction(() => {
    this.error = 'Invalid code or access expired.';
    this.CurrentJudge = null;
    sessionStorage.removeItem('currentJudge');
    sessionStorage.removeItem(JUDGE_SESSION_EXPIRES_AT);
  });

  return null;
} finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

   async getJudgeEventHome(
    eventJudgeId: number,
    eventId: number
  ): Promise<EventJudgeEventHomeDTO | null> {
    this.loading = true;
    this.error = null;

    try {
      // replace this method name with the exact generated client method
      const result = await this.EventJudgeService.eventhomeDetail(eventJudgeId, eventId);

      runInAction(() => {
        this.JudgeEventHome = result?.data ?? null;
      });

      return result?.data ?? null;
    } catch (error) {
      console.error('Error fetching judge event home:', error);
      runInAction(() => {
        this.error = 'Failed to load judge event home.';
        this.JudgeEventHome = null;
      });
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async updateLoggedInJudgeProfile(
  eventJudgeId: number,
  payload: UpdateJudgeProfileRequest
): Promise<EventJudgeModel | null> {
  this.loading = true;
  this.error = null;

  try {
    const result = await this.EventJudgeService.profileUpdate(eventJudgeId, payload);

    const updatedJudge = result?.data ?? null;

    runInAction(() => {
      this.CurrentJudge = updatedJudge;

      if (updatedJudge) {
        sessionStorage.setItem('currentJudge', JSON.stringify(updatedJudge));
      }
    });

    return updatedJudge;
  } catch (error) {
    console.error('Error updating logged in judge profile:', error);

    runInAction(() => {
      this.error = 'Failed to update judge profile.';
    });

    return null;
  } finally {
    runInAction(() => {
      this.loading = false;
    });
  }
}

async finishSideAwards(
  pageantId: number,
  divisionId: number,
  ageGroupId?: number | null,
): Promise<boolean> {
  try {
    const response =
      await this.TabulationService
        .finishSideAwardsPageantDivisionCreate(
          pageantId,
          divisionId,
          {
            ageGroupId:
              ageGroupId ?? undefined,
          },
        );

    if (!response.ok) {
      console.warn(
        '[FinishSideAwards] Side Awards are not complete.',
        {
          pageantId,
          divisionId,
          ageGroupId,
        },
      );

      return false;
    }

    console.log(
      '[FinishSideAwards] Complete',
      {
        pageantId,
        divisionId,
        ageGroupId,
      },
    );

    return true;
  } catch (error) {
    console.error(
      '[FinishSideAwards] Error:',
      error,
    );

    return false;
  }
}

async sendJudgeTabulatorHelpMessage(payload: {
  eventJudgeId?: number;
  eventId?: number;
  judgeLabel?: string;
  judgeName?: string;
  accessCodeLast4?: string;
  eventName?: string;
  message: string;
}): Promise<boolean> {
  this.loading = true;
  this.error = null;

  try {
    await (this.EventJudgeService as any).sendHelpMessageCreate(payload);
    return true;
  } catch (error) {
    console.error('Error sending judge tabulator help message:', error);

    runInAction(() => {
      this.error = 'Failed to send message.';
    });

    return false;
  } finally {
    runInAction(() => {
      this.loading = false;
    });
  }
}

async sendJudgeLoginHelpMessage(payload: {
  judgeName: string;
  eventName: string;
  message: string;
}): Promise<boolean> {
  this.loading = true;
  this.error = null;

  try {
    await (this.EventJudgeService as any).sendHelpMessageCreate({
      eventJudgeId: 0,
      eventId: 0,
      judgeName: payload.judgeName,
      eventName: payload.eventName,
      message: payload.message,
    });

    return true;
  } catch (error) {
    console.error('Error sending judge login help message:', error);

    runInAction(() => {
      this.error = 'Failed to send message.';
    });

    return false;
  } finally {
    runInAction(() => {
      this.loading = false;
    });
  }
}

async logoutInactiveJudgeAndClearSyncedData() {
  const currentJudge = this.CurrentJudge;

  this.CurrentJudge = null;
  this.error = null;

  sessionStorage.removeItem('currentJudge');
  sessionStorage.removeItem(JUDGE_SESSION_EXPIRES_AT);

  try {
    if (currentJudge?.id && currentJudge?.eventId) {
      await localScoreRepository.deleteAllJudgeOfflineDataIfSynced({
        eventJudgeId: Number(currentJudge.id),
        eventId: Number(currentJudge.eventId),
      });
    }
  } catch (error) {
    console.warn('Failed clearing inactive judge offline data', error);
  }
}
}

let eventJudgeStore: EventJudgeStore;

export function GetEventJudgeStore() {
  if (!eventJudgeStore) {
    eventJudgeStore = new EventJudgeStore();
  }
  return eventJudgeStore;
}